import PreviredAPIClientMock from "./previred-api-mock";
import {
  VALIDATION_TYPE,
  isValidationError,
  parseApiResponse,
  validateClientValidationId,
  validateExecutiveValidationId
} from "./previred/validation-id";

const PREVIRED_MODE = {
  DATA: "DATA",
  TOKEN: "TOKEN"
};

const VALIDATION = {
  ACEPTA_BIOMETRIC: {
    validationType: 1001,
    provider: "ACEPTA"
  },
  FACIAL_RECOGNITION: {
    validationType: 1003,
    provider: "TOC"
  },
  CICUE: {
    authMethod: "CICUE",
    validationType: 1005,
    provider: "ECERT"
  }
};

class PreviredAPIClient {
  constructor(previredApiUrl, kongService) {
    this.kongService = kongService;
    this.previredApiUrl = previredApiUrl;
  }

  async requestConfig() {
    const token = await this.kongService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    };
  }

  async submitPreviredDataRequest(client, executive, validation, validationIds = {}) {
    const ticketRequest = {
      client: { ...client, url: client.url.replace("\n", "") },
      provider: validation.provider
    };
    if (executive) {
      const clientCheck = validateClientValidationId(
        validationIds.clientValidationId,
        validation.validationType
      );
      if (!clientCheck.valid) throw clientCheck.error;
      const executiveCheck = validateExecutiveValidationId(
        validationIds.executiveValidationId,
        VALIDATION_TYPE.BIOMETRIC
      );
      if (!executiveCheck.valid) throw executiveCheck.error;
      ticketRequest.clientValidationType = validation.validationType;
      ticketRequest.executiveValidationType = VALIDATION_TYPE.BIOMETRIC;
      ticketRequest.clientValidationId = clientCheck.value;
      ticketRequest.executiveValidationId = executiveCheck.value;
      ticketRequest.executive = {
        name: executive.name,
        lastName: executive.lastName,
        motherLastName: executive.motherLastName,
        rut: executive.rut,
        auditCode: executive.auditCode,
        url: executive.url.replace("\n", "")
      };
    } else {
      ticketRequest.validationType = validation.validationType;
    }
    const fetchConfig = await this.requestConfig();
    return fetch(`${this.previredApiUrl}/api/v1/validate/${executive ? "assisted" : "self"}`,
      {
        method: "POST",
        headers: fetchConfig.headers,
        body: JSON.stringify({ ticketRequest })
      }
    ).then(response => parseApiResponse(response));
  }

  async getPreviredToken(transactionId) {
    const fetchConfig = await this.requestConfig();
    return fetch(`${this.previredApiUrl}/api/v1/token?transactionId=${transactionId}`,
      {
        method: "GET",
        headers: fetchConfig.headers
      }
    ).then(response => response.json());
  }
}

class PreviredAPI {
  constructor(previredApiUrl, kongService, useMock = false) {
    this.kongService = kongService;
    this.previredAPIClient = useMock
      ? new PreviredAPIClientMock()
      : new PreviredAPIClient(previredApiUrl, this.kongService);
  }

  pollingForPreviredData(transactionId) {
    const intervalInMillis = parseInt(process.env.VUE_APP_PREVIRED_POLLING_INTERVAL);
    const errorCounterThreshold = parseInt(process.env.VUE_APP_PREVIRED_POLLING_ERROR_THRESHOLD);
    const maxTime = parseInt(process.env.VUE_APP_PREVIRED_POLLING_MAX_TIME);
    const previredAPI = this;
    return new Promise((resolve, reject) => {
      let errorCounter = 0;
      function poll(startTime) {
        let timePassed = Date.now() - startTime;
        if (timePassed < maxTime) {
          setTimeout(() => {
            previredAPI.previredAPIClient
              .getPreviredToken(transactionId)
              .then(completeResponse => {
                const response = completeResponse;
                if (response.status === "OK") {
                  resolve({
                    transactionId,
                    clientIP: response.clientIP,
                    executiveIP: response.executiveIP,
                    clientProvider: response.clientProvider
                  });
                } else if (response.status === "PENDING") {
                  poll(startTime);
                } else {
                  reject(
                    `Fulfillment PreviredAPI returned ${response.status} status for transactionId ${transactionId}`
                  );
                }
              })
              .catch(error => {
                errorCounter += 1;
                if (errorCounter >= errorCounterThreshold) {
                  if (error.message) reject(`${error.message}: ${error.reason}`);
                  else
                    reject(
                      `Unknown error calling Fulfillment PreviredAPI: ${JSON.stringify(error)}`
                    );
                } else {
                  poll(startTime);
                }
              });
          }, intervalInMillis);
        } else {
          reject(`Timout (${maxTime}) trying to request previred data`);
        }
      }
      poll(Date.now());
    });
  }

  getPreviredResponse(previredAuthResponse, validation) {
    return new Promise((resolve, reject) => {
      if (previredAuthResponse.status === "OK") {
        return this.previredAPIClient.submitPreviredDataRequest(
          previredAuthResponse.client,
          previredAuthResponse.executive,
          validation,
          {
            clientValidationId: previredAuthResponse.clientValidationId,
            executiveValidationId: previredAuthResponse.executiveValidationId
          }
        ).then(submitResponse => {
            const transactionId = submitResponse.transactionId;
            if (transactionId) {
              this.pollingForPreviredData(transactionId)
                .then(resolve)
                .catch(error => reject(`Error polling from previred-api ${error}`));
            } else {
              reject(
                `No transactionId obtained from data submit in previred-api ${submitResponse}`
              );
            }
          }
        ).catch(error => {
            reject(
              isValidationError(error)
                ? error
                : `Error submitting data to previred-api: ${JSON.stringify(error)}`
            );
          });
      } else {
        reject(`Unexpected previred auth response ${JSON.stringify(previredAuthResponse)}`);
      }
    });
  }
}

export { PREVIRED_MODE, PreviredAPI, VALIDATION };

